import { useTranslation } from "react-i18next";
import { Group, Loader, Modal, PasswordInput, Stack, Text, TextInput } from "@mantine/core";
import { hasLength, isEmail, isNotEmpty, useForm } from "@mantine/form";
import { useCaptcha } from "../api/captcha";
import type { ApiError } from "../api/client";
import { useRegister } from "../api/queries";
import { MIN_PASSWORD_LENGTH } from "../api/types";
import { useLanguage } from "../i18n/useLanguage";
import { Chip } from "./Chip";
import { FeedbackLine } from "./FeedbackLine";

export function RegisterDialog({ onClose, onLogin }: { onClose: () => void; onLogin: () => void }) {
  const { t } = useTranslation();
  const register = useRegister();
  const { guard, verifying, failed } = useCaptcha();
  // The language the person actually filled the form in, rather than a server-side parse of
  // Accept-Language: it is the browser-detected one unless they overrode it.
  const language = useLanguage();
  const form = useForm({
    mode: "uncontrolled",
    initialValues: { username: "", email: "", password: "" },
    validate: {
      username: isNotEmpty(t("registerDialog.usernameRequired")),
      // The address is required, and `isEmail` rejects the empty string as well, so one rule
      // covers both -- as does the length rule below, which no empty password can pass.
      email: isEmail(t("registerDialog.emailInvalid")),
      password: hasLength(
        { min: MIN_PASSWORD_LENGTH },
        t("registerDialog.passwordTooShort", { min: MIN_PASSWORD_LENGTH }),
      ),
    },
  });

  return (
    <Modal opened onClose={onClose} title={t("registerDialog.heading")}>
      {/* The dialog stays open on success and shows exactly what the server said. It
          deliberately does not claim the account was created: the same answer comes back
          when the address already has one, because saying otherwise would turn this form
          into a way to test whether any given address is a member. Nothing is reset either:
          this branch replaces the form rather than clearing it. */}
      {register.isSuccess ? (
        <Stack gap="sm">
          <Text size="sm">{register.data.detail}</Text>
          <Group justify="flex-end" gap="xs">
            <Chip onClick={onClose}>{t("common.close")}</Chip>
          </Group>
        </Stack>
      ) : (
        // `noValidate` so the address field keeps `type="email"` for the phone keyboard
        // without the browser also refusing the submit in its own language, which is the
        // whole reason these rules moved into the catalogue.
        <form
          noValidate
          onSubmit={form.onSubmit(
            (values) =>
              void guard((captcha_token) =>
                register.mutateAsync({
                  ...values,
                  language,
                  ...(captcha_token ? { captcha_token } : {}),
                }),
              ),
          )}
        >
          <Stack gap="sm">
            <TextInput
              data-autofocus
              label={t("registerDialog.username")}
              name="username"
              autoComplete="username"
              maxLength={64}
              key={form.key("username")}
              {...form.getInputProps("username")}
            />
            <TextInput
              type="email"
              label={t("registerDialog.email")}
              name="email"
              autoComplete="email"
              key={form.key("email")}
              {...form.getInputProps("email")}
            />
            <PasswordInput
              label={t("registerDialog.password")}
              name="password"
              autoComplete="new-password"
              description={t("registerDialog.passwordHint", { count: MIN_PASSWORD_LENGTH })}
              key={form.key("password")}
              {...form.getInputProps("password")}
            />

            {/* One slot, three states. The captcha's own "required" refusal never reaches
                the user: it is an instruction to this component, not a message. */}
            <FeedbackLine role={verifying ? "status" : "alert"} c={verifying ? "dimmed" : "danger"}>
              {verifying ? (
                <Group gap="xs" wrap="nowrap">
                  <Loader size={14} />
                  <Text inherit>{t("captcha.verifying")}</Text>
                </Group>
              ) : failed ? (
                t("captcha.failed")
              ) : (
                register.isError &&
                (register.error as ApiError).captcha == null &&
                (register.error as Error).message
              )}
            </FeedbackLine>

            <Group justify="flex-end" gap="xs">
              <Chip onClick={onLogin}>{t("registerDialog.haveAccount")}</Chip>
              <Chip type="submit" variant="accent" disabled={register.isPending || verifying}>
                {register.isPending || verifying
                  ? t("registerDialog.sending")
                  : t("registerDialog.submit")}
              </Chip>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
